/**
 * Página pública /v/:codigo — valida el voucher escaneado por el QR.
 *
 * Sin login. Diseñada para ser abierta en el muelle desde un celular:
 * grandes tipografías, estado claro (válido / no válido), datos del viaje.
 *
 * Mobile-first.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Chip, Stack, CircularProgress, Alert, Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import PaidIcon from '@mui/icons-material/Paid';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function fmtFecha(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso + 'T12:00:00').toLocaleDateString('es-CO', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
  } catch { return iso; }
}

function fmtMoney(n, m = 'COP') {
  try {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency', currency: m, maximumFractionDigits: 0,
    }).format(Number(n) || 0);
  } catch { return `$${n}`; }
}

export default function VoucherPublico() {
  const { codigo } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/turismo/voucher-publico/${encodeURIComponent(codigo)}`)
      .then(async r => {
        if (!r.ok) {
          const e = await r.json().catch(() => ({}));
          throw new Error(e.detail || 'No encontramos esta reserva.');
        }
        return r.json();
      })
      .then(d => setData(d))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [codigo]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
                 background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' }}>
        <CircularProgress sx={{ color: '#fff' }} />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
                 p: 3, background: '#fef2f2' }}>
        <Paper sx={{ p: 4, maxWidth: 420, textAlign: 'center', borderRadius: 3 }}>
          <CancelIcon sx={{ fontSize: 64, color: '#dc2626', mb: 1 }} />
          <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#0f172a', mb: 1 }}>
            Voucher no encontrado
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#64748b' }}>
            {error || 'El código no corresponde a una reserva activa.'}
          </Typography>
          <Typography sx={{ fontSize: 11, color: '#94a3b8', mt: 2, fontFamily: 'monospace' }}>
            Código: {codigo}
          </Typography>
        </Paper>
      </Box>
    );
  }

  const esValido = data.valida;
  const colorPrincipal = esValido ? '#0d9488' : '#dc2626';
  const colorBg = esValido
    ? 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)'
    : 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)';

  return (
    <Box sx={{ minHeight: '100vh', background: '#f8fafc', pb: 4 }}>
      {/* Hero con estado */}
      <Box sx={{ background: colorBg, color: '#fff', px: 3, pt: 4, pb: 5,
                 borderBottomLeftRadius: 24, borderBottomRightRadius: 24 }}>
        <Box sx={{ textAlign: 'center' }}>
          {esValido ? (
            <CheckCircleIcon sx={{ fontSize: 56, mb: 1 }} />
          ) : (
            <CancelIcon sx={{ fontSize: 56, mb: 1 }} />
          )}
          <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: 0.5 }}>
            {esValido ? 'VOUCHER VÁLIDO' : 'NO VÁLIDO'}
          </Typography>
          <Typography sx={{ fontSize: 12, opacity: 0.9, mt: 0.5 }}>
            Estado: {data.estado}
          </Typography>
          {data.empresa && (
            <Typography sx={{ fontSize: 11, opacity: 0.85, mt: 1.5, textTransform: 'uppercase', letterSpacing: 1 }}>
              {data.empresa}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ px: 2, mt: -3 }}>
        {/* Tarjeta principal */}
        <Paper sx={{ p: 2.5, borderRadius: 3, mb: 2, boxShadow: '0 8px 24px rgba(0,0,0,0.08)' }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <ConfirmationNumberIcon sx={{ color: colorPrincipal }} />
            <Typography sx={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 1 }}>
              Código de reserva
            </Typography>
          </Stack>
          <Typography sx={{
            fontSize: 26, fontWeight: 800, fontFamily: 'monospace',
            color: colorPrincipal, mb: 2, letterSpacing: 1,
          }}>
            {data.codigo_reserva}
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <Typography sx={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700, mb: 0.5 }}>
            Titular
          </Typography>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#0f172a', mb: 0.25 }}>
            {data.cliente?.nombre || '—'}
          </Typography>
          {data.cliente?.contacto && (
            <Typography sx={{ fontSize: 12, color: '#64748b' }}>
              WhatsApp: {data.cliente.contacto}
            </Typography>
          )}
        </Paper>

        {/* Detalles del viaje */}
        <Paper sx={{ p: 2.5, borderRadius: 3, mb: 2 }}>
          <Typography sx={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 1, mb: 1.5 }}>
            Detalles del viaje
          </Typography>

          <Stack spacing={1.5}>
            <Detalle icon={<EventIcon />} label="Fecha" valor={fmtFecha(data.viaje?.fecha)} />
            <Detalle icon={<AccessTimeIcon />} label="Horario"
              valor={`${data.viaje?.hora_salida || '—'} → ${data.viaje?.hora_regreso || '—'}`} />
            <Detalle icon={<LocationOnIcon />} label="Destino"
              valor={data.viaje?.destino || data.viaje?.paquete || '—'}
              sub={data.viaje?.paquete && data.viaje?.destino ? data.viaje.paquete : null} />
            <Detalle icon={<LocationOnIcon />} label="Punto de embarque"
              valor={data.viaje?.punto_embarque || '—'}
              sub={data.viaje?.direccion_embarque} />
            <Detalle icon={<PeopleAltIcon />} label="Pasajeros"
              valor={`${data.pasajeros?.total || 0} persona(s)`}
              sub={`${data.pasajeros?.adultos || 0} adultos · ${data.pasajeros?.ninos || 0} niños · ${data.pasajeros?.bebes || 0} bebés`} />
          </Stack>
        </Paper>

        {/* Saldo */}
        {data.saldo_pendiente > 0 && (
          <Alert severity="warning" icon={<PaidIcon />}
            sx={{ mb: 2, borderRadius: 3, fontSize: 13 }}>
            <b>Saldo pendiente:</b> {fmtMoney(data.saldo_pendiente, data.moneda)} — se cobra en el muelle.
          </Alert>
        )}

        {!esValido && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 3 }}>
            Esta reserva está <b>{data.estado}</b>. Verifica con el coordinador antes de embarcar.
          </Alert>
        )}

        <Typography sx={{ fontSize: 10, color: '#94a3b8', textAlign: 'center', mt: 3 }}>
          Validación automática desde QR · {data.empresa}
        </Typography>
      </Box>
    </Box>
  );
}

function Detalle({ icon, label, valor, sub }) {
  return (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
      <Box sx={{ color: '#0d9488', mt: 0.25 }}>{icon}</Box>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>
          {valor}
        </Typography>
        {sub && (
          <Typography sx={{ fontSize: 11, color: '#64748b', mt: 0.25 }}>
            {sub}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
